/**
 * A design to the HTML that is actually sent.
 *
 * Every choice here is a mail-client concession, not a preference:
 *  - Layout is `<table>`, not flex or grid. Outlook renders through Word, which
 *    has neither.
 *  - Styles are inline. Gmail strips `<style>` blocks in several contexts, and
 *    a stripped stylesheet turns a letter into unstyled text.
 *  - Rounded corners and background colours sit on a `<td>`, not on a wrapping
 *    `<div>`. Gmail on Android drops border-radius from the div.
 *  - Buttons are a table with the background on the cell, with the anchor
 *    inside. Gmail Android strips `display:inline-block` from an anchor, which
 *    turns a plain styled `<a>` into a full-width bar.
 *  - Columns become `<td>` siblings with percentage widths. They do NOT stack
 *    on a phone, because the only way to make them stack is a media query and
 *    Gmail ignores those - so the editor caps the count instead of promising
 *    responsiveness it cannot deliver.
 *
 * The canvas renders each block with the very same function that builds the
 * letter, so what the operator approves is what goes out. That is the reason
 * this file has no React in it and returns strings.
 */
import {
  fontStack,
  resolveProps,
  type Align,
  type Block,
  type ColumnSlot,
  type EmailDesign,
  type PageStyle,
} from "./types";

/** The token the sender replaces with this recipient's own opt-out URL.
 *  Mirrors `crm/sections/marketing/detail/readiness.ts`. */
export const UNSUB_TOKEN = "{{unsubscribe}}";

export function escapeHtml(s: unknown): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * A URL that is safe to put in an href.
 *
 * Anything that is not http, https, mailto, tel or a merge token becomes "#".
 * `javascript:` in a letter is stripped by every mail client anyway, but the
 * canvas renders the same string in the CRM, where it would not be.
 */
export function safeUrl(raw: unknown): string {
  const url = String(raw ?? "").trim();
  if (!url) return "";
  if (url.startsWith("{{")) return url;
  if (/^(https?:|mailto:|tel:|#)/i.test(url)) return url;
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return "#";
  return `https://${url}`;
}

/**
 * Rich text on its way into a letter.
 *
 * Scripts and inline event handlers go, in the OUTPUT rather than only in the
 * preview: a mail client would strip them, but the canvas shows this same
 * string inside the CRM, and a letter carrying an `onerror` is not something to
 * hand to a transport either. Headings become styled paragraphs because
 * client stylesheets reset `<h1>` margins in mutually incompatible ways.
 */
const HEADING_PX: Record<string, number> = { h1: 30, h2: 24, h3: 20, h4: 18, h5: 16, h6: 15 };

export function sanitizeRichHtml(raw: unknown): string {
  return String(raw ?? "")
    .replace(/<script[\s\S]*?<\/script\s*>/gi, "")
    .replace(/<\/?(?:script|iframe|object|embed|form)\b[^>]*>/gi, "")
    .replace(/\son[a-z]+\s*=\s*"[^"]*"/gi, "")
    .replace(/\son[a-z]+\s*=\s*'[^']*'/gi, "")
    .replace(/\son[a-z]+\s*=\s*[^\s>]+/gi, "")
    .replace(/<(h[1-6])([^>]*)>([\s\S]*?)<\/\1\s*>/gi, (_m, tag: string, attrs: string, inner: string) => {
      const px = HEADING_PX[tag.toLowerCase()] ?? 16;
      return `<p${attrs} style="margin:0 0 0.5em;font-size:${px}px;font-weight:700;line-height:1.25;">${inner}</p>`;
    });
}

function pad(props: Record<string, unknown>): string {
  const top = Number(props.paddingTop ?? 0);
  const bottom = Number(props.paddingBottom ?? 0);
  return `padding:${top}px 0 ${bottom}px;`;
}

function align(props: Record<string, unknown>): Align {
  const a = props.align;
  return a === "center" || a === "right" ? a : "left";
}

/** A block-level colour falls back to the letter's own. Storing "" rather than
 *  a copy of the page colour is what lets a palette change reach old blocks. */
function color(value: unknown, fallback: string): string {
  const v = String(value ?? "").trim();
  return v || fallback;
}

function size(value: unknown, fallback: number): number {
  const n = Number(value ?? 0);
  return n > 0 ? n : fallback;
}

// ─────────────────────────────────────────────────────────────────────────
// Blocks
// ─────────────────────────────────────────────────────────────────────────

/**
 * One block as the letter will contain it.
 *
 * `width` is the pixel width available to the block - the card's inner width at
 * the top level, one column's share inside `columns`. Images need it: a mail
 * client that ignores `max-width` still honours the `width` attribute, and
 * without a real number that attribute is a guess.
 */
export function renderBlock(block: Block, page: PageStyle, width: number): string {
  const p = resolveProps(block);
  const font = fontStack(page.fontFamily);

  switch (block.type) {
    case "heading": {
      const level = String(p.level ?? "h2");
      const px = size(p.fontSize, HEADING_PX[level] ?? 24);
      return (
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>` +
        `<td style="${pad(p)}text-align:${align(p)};font-family:${font};">` +
        `<p style="margin:0;font-size:${px}px;line-height:1.25;font-weight:700;color:${color(p.color, page.textColor)};">` +
        `${escapeHtml(p.text)}</p></td></tr></table>`
      );
    }

    case "text": {
      const px = size(p.fontSize, page.fontSize);
      const lh = Number(p.lineHeight ?? 1.6);
      return (
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>` +
        `<td style="${pad(p)}text-align:${align(p)};font-family:${font};font-size:${px}px;` +
        `line-height:${lh};color:${color(p.color, page.textColor)};">` +
        `${sanitizeRichHtml(p.html)}</td></tr></table>`
      );
    }

    case "button": {
      const href = safeUrl(p.url) || "#";
      const full = p.fullWidth === true;
      const inner = `padding:${Number(p.innerY ?? 12)}px ${Number(p.innerX ?? 24)}px;`;
      // Bulletproof pattern: the outer cell aligns, the inner table carries the
      // background and the radius, the anchor only carries the text.
      return (
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>` +
        `<td align="${align(p)}" style="${pad(p)}">` +
        `<table role="presentation" ${full ? 'width="100%" ' : ""}cellpadding="0" cellspacing="0" border="0" ` +
        `style="border-collapse:separate;"><tr>` +
        `<td align="center" bgcolor="${escapeHtml(p.backgroundColor)}" ` +
        `style="background-color:${escapeHtml(p.backgroundColor)};border-radius:${Number(p.radius ?? 6)}px;${inner}">` +
        `<a href="${escapeHtml(href)}" target="_blank" rel="noopener" ` +
        `style="display:inline-block;text-decoration:none;font-family:${font};font-size:${page.fontSize - 1}px;` +
        `font-weight:700;color:${escapeHtml(p.textColor)};white-space:nowrap;">${escapeHtml(p.text)}</a>` +
        `</td></tr></table></td></tr></table>`
      );
    }

    case "image": {
      const src = safeUrl(p.src);
      if (!src) return "";
      const pctWide = Math.min(100, Math.max(10, Number(p.width ?? 100)));
      const px = Math.round((width * pctWide) / 100);
      const img =
        `<img src="${escapeHtml(src)}" alt="${escapeHtml(p.alt)}" width="${px}" ` +
        `style="width:${px}px;max-width:100%;height:auto;display:block;border:0;outline:none;` +
        `border-radius:${Number(p.radius ?? 0)}px;">`;
      const href = safeUrl(p.href);
      const body = href ? `<a href="${escapeHtml(href)}" target="_blank" rel="noopener">${img}</a>` : img;
      return (
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>` +
        `<td align="${align(p)}" style="${pad(p)}font-size:0;line-height:0;">${body}</td></tr></table>`
      );
    }

    case "divider":
      return (
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>` +
        `<td style="${pad(p)}"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">` +
        `<tr><td style="border-top:${Number(p.thickness ?? 1)}px solid ${escapeHtml(p.color)};font-size:0;line-height:0;">` +
        `&nbsp;</td></tr></table></td></tr></table>`
      );

    case "spacer": {
      const h = Number(p.height ?? 24);
      return (
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>` +
        `<td height="${h}" style="height:${h}px;font-size:0;line-height:0;">&nbsp;</td></tr></table>`
      );
    }

    case "columns": {
      const cols = (p.cols as ColumnSlot[] | undefined) ?? [];
      if (cols.length === 0) return "";
      const gap = Number(p.gap ?? 24);
      const share = Math.floor(100 / cols.length);
      // The pixel budget a child gets: its share of the card, minus the gutters
      // it does not own. Passed down so images inside a column size honestly.
      const childWidth = Math.max(80, Math.floor((width - gap * (cols.length - 1)) / cols.length));
      const cells = cols
        .map((col, i) => {
          const left = i === 0 ? 0 : Math.round(gap / 2);
          const right = i === cols.length - 1 ? 0 : Math.round(gap / 2);
          const inner = (col.children ?? []).map((c) => renderBlock(c, page, childWidth)).join("");
          return (
            `<td width="${share}%" valign="top" style="width:${share}%;padding:0 ${right}px 0 ${left}px;` +
            `vertical-align:top;">${inner}</td>`
          );
        })
        .join("");
      return (
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="${pad(p)}">` +
        `<tr>${cells}</tr></table>`
      );
    }

    case "quote": {
      const author = String(p.author ?? "").trim();
      return (
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="${pad(p)}"><tr>` +
        `<td width="4" bgcolor="${escapeHtml(p.borderColor)}" ` +
        `style="width:4px;background-color:${escapeHtml(p.borderColor)};font-size:0;line-height:0;">&nbsp;</td>` +
        `<td style="background-color:${escapeHtml(p.backgroundColor)};padding:14px 18px;font-family:${font};">` +
        `<p style="margin:0;font-size:${page.fontSize - 1}px;line-height:1.55;font-style:italic;` +
        `color:${color(p.color, page.textColor)};">${escapeHtml(p.text)}</p>` +
        (author
          ? `<p style="margin:8px 0 0;font-size:${page.fontSize - 3}px;font-weight:600;color:${color(p.color, page.textColor)};">` +
            `${escapeHtml(author)}</p>`
          : "") +
        `</td></tr></table>`
      );
    }

    case "list": {
      const items = String(p.items ?? "")
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
      if (items.length === 0) return "";
      const marker = String(p.marker ?? "•");
      const c = color(p.color, page.textColor);
      // Rows rather than <ul>: client stylesheets disagree about list padding,
      // and an indent that changes per inbox breaks the letter's left edge.
      const rows = items
        .map(
          (item) =>
            `<tr><td width="20" valign="top" style="width:20px;padding:3px 0;font-family:${font};` +
            `font-size:${page.fontSize}px;line-height:1.55;color:${c};">${escapeHtml(marker)}</td>` +
            `<td valign="top" style="padding:3px 0;font-family:${font};font-size:${page.fontSize}px;` +
            `line-height:1.55;color:${c};">${escapeHtml(item)}</td></tr>`,
        )
        .join("");
      return (
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" ` +
        `style="${pad(p)}text-align:${align(p)};"><tbody>${rows}</tbody></table>`
      );
    }

    case "social": {
      const links = ((p.links as { id: string; label: string; url: string }[] | undefined) ?? []).filter(
        (l) => String(l.url ?? "").trim(),
      );
      if (links.length === 0) return "";
      const c = color(p.color, page.textColor);
      // Text links, not icon images. Images hosted for the sole purpose of
      // sitting in a cold letter are blocked by default in most inboxes, so an
      // icon row arrives as a line of empty boxes.
      const body = links
        .map(
          (l) =>
            `<a href="${escapeHtml(safeUrl(l.url))}" target="_blank" rel="noopener" ` +
            `style="color:${c};font-family:${font};font-size:${page.fontSize - 3}px;text-decoration:underline;` +
            `padding:0 8px;">${escapeHtml(l.label)}</a>`,
        )
        .join("");
      return (
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>` +
        `<td align="${align(p)}" style="${pad(p)}">${body}</td></tr></table>`
      );
    }

    case "signature": {
      const avatar = safeUrl(p.avatar);
      const lines = [
        String(p.title ?? "").trim(),
        String(p.email ?? "").trim(),
        String(p.phone ?? "").trim(),
      ].filter(Boolean);
      const detail = lines
        .map(
          (l) =>
            `<p style="margin:2px 0 0;font-size:${page.fontSize - 3}px;line-height:1.5;color:#667085;">${escapeHtml(l)}</p>`,
        )
        .join("");
      return (
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>` +
        `<td align="${align(p)}" style="${pad(p)}">` +
        `<table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr>` +
        (avatar
          ? `<td width="56" valign="middle" style="width:56px;padding-right:14px;">` +
            `<img src="${escapeHtml(avatar)}" alt="${escapeHtml(p.name)}" width="56" height="56" ` +
            `style="width:56px;height:56px;border-radius:28px;display:block;border:0;"></td>`
          : "") +
        `<td valign="middle" style="font-family:${font};text-align:left;">` +
        `<p style="margin:0;font-size:${page.fontSize - 1}px;font-weight:700;line-height:1.3;` +
        `color:${page.textColor};">${escapeHtml(p.name)}</p>${detail}` +
        `</td></tr></table></td></tr></table>`
      );
    }

    case "footer": {
      const px = size(p.fontSize, 12);
      const c = color(p.color, "#98a2b3");
      const address = String(p.address ?? "").trim();
      return (
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>` +
        `<td align="${align(p)}" style="${pad(p)}font-family:${font};font-size:${px}px;line-height:1.6;color:${c};">` +
        `<p style="margin:0;color:${c};">${escapeHtml(p.text)}</p>` +
        (address ? `<p style="margin:6px 0 0;color:${c};">${escapeHtml(address)}</p>` : "") +
        `<p style="margin:6px 0 0;"><a href="${UNSUB_TOKEN}" ` +
        `style="color:${c};text-decoration:underline;">${escapeHtml(p.unsubscribeText)}</a></p>` +
        `</td></tr></table>`
      );
    }

    case "html":
      return (
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>` +
        `<td style="${pad(p)}font-family:${font};font-size:${page.fontSize}px;color:${page.textColor};">` +
        `${sanitizeRichHtml(p.html)}</td></tr></table>`
      );

    default:
      return "";
  }
}

/** The pixel width a top-level block has to work with. */
export function innerWidth(page: PageStyle): number {
  return Math.max(120, page.contentWidth - page.paddingX * 2);
}

// ─────────────────────────────────────────────────────────────────────────
// Document
// ─────────────────────────────────────────────────────────────────────────

/**
 * The whole letter.
 *
 * A full document rather than a fragment: a mail client is handed a body, and
 * one without `<meta charset>` renders Estonian letters as mojibake in Outlook.
 *
 * The preheader is deliberately NOT injected here. The campaign carries its own
 * `preheader` field and the sender is the one that places it; putting it in the
 * body too would show the line twice the day M2 starts doing its half.
 */
export function renderEmailHtml(design: EmailDesign): string {
  const page = design.page;
  const width = innerWidth(page);
  const font = fontStack(page.fontFamily);
  const body = design.blocks.map((b) => renderBlock(b, page, width)).join("");

  return (
    `<!doctype html><html><head><meta charset="utf-8">` +
    `<meta name="viewport" content="width=device-width,initial-scale=1">` +
    `<meta name="color-scheme" content="light only">` +
    `<meta name="supported-color-schemes" content="light only"></head>` +
    `<body style="margin:0;padding:0;background-color:${escapeHtml(page.backgroundColor)};">` +
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" ` +
    `style="background-color:${escapeHtml(page.backgroundColor)};"><tr><td align="center" style="padding:24px 12px;">` +
    `<table role="presentation" width="${page.contentWidth}" cellpadding="0" cellspacing="0" border="0" ` +
    `style="width:${page.contentWidth}px;max-width:100%;"><tr>` +
    `<td bgcolor="${escapeHtml(page.cardColor)}" style="background-color:${escapeHtml(page.cardColor)};` +
    `border-radius:${page.radius}px;padding:${page.paddingY}px ${page.paddingX}px;font-family:${font};` +
    `font-size:${page.fontSize}px;color:${escapeHtml(page.textColor)};">` +
    body +
    `</td></tr></table></td></tr></table></body></html>`
  );
}
